/**
 * Marke „Entwicklung <Zahl>“ in der Kopfzeile — mit der Liste der laufenden Arbeiten. (16.10.2026)
 *
 * Kommt vom Chat-Strom (Ereignis `schmied-arbeit`, server/schmied-arbeit.ts). Kein Chat-Beitrag, kein Eintrag im Verlauf.
 * Die Marke ist ein echter <button> (aria-expanded), sichtbar nur, solange mindestens eine Arbeit läuft. Die Liste öffnet beim
 * Darüberfahren (Maus), mit der Tastatur-Fokus und beim Antippen; Escape oder ein Tipp daneben schließt. Jeder Text geht
 * ausschließlich über textContent hinein. Die Zeit „seit 12 Min“ rechnet die Seite aus ihrer eigenen Uhr (Server sendet die Dauer).
 *
 * 22.10.2026 (Martin: „Ich sehe nur einen Prozess in der Entwicklung“): Die Tafel ist eine ruhige Liste der Hilfskräfte — je Zeile ein
 * Zeichen für den Stand (Punkt, Ring, halber Punkt, Haken; in style.css gezeichnet, für Screenreader verborgen), der Titel und darunter
 * der Stand in Worten mit Startzeit und Dauer. Wer auf Martin wartet, steht oben; „fertig“ steht gedämpft unten. Die Zahl der Marke zählt
 * nur die aktiven (nicht „fertig“). Reine Anzeige: kein Knopf in der Tafel.
 */
import {
  SCHMIED_ARBEIT_HOECHSTENS,
  SCHMIED_ARBEIT_STUFE_NAME,
  SCHMIED_ARBEIT_VERFALL_MS,
  istAktiv,
  type SchmiedArbeit,
  type SchmiedArbeitStand,
} from "../../shared/schmied-arbeit.js";
import { beobachteEntwicklungModell, zeigeEntwicklungModell } from "./entwicklung-modell.js";

const MARKE_ID = "topbar-entwicklung";
const ZAHL_ID = "topbar-entwicklung-zahl";
const LISTE_ID = "topbar-entwicklung-liste";
const SUMME_ID = "topbar-entwicklung-summe";
const SCHLIESS_VERZOEGERUNG_MS = 220;
const AUFFRISCHEN_MS = 30_000;

interface Gemerkt extends Omit<SchmiedArbeit, "seitMs"> {
  /** Beginn nach der Uhr dieser Seite. */
  start: number;
  /** Nur „fertig“: die ganze Dauer (wächst nicht mehr). */
  dauerMs?: number;
}

let arbeiten: Gemerkt[] = [];
let verfallsUhr: ReturnType<typeof setTimeout> | null = null;
let auffrischUhr: ReturnType<typeof setInterval> | null = null;
let schliessUhr: ReturnType<typeof setTimeout> | null = null;
const verdrahtet = new WeakSet<Element>();
const dokumentVerdrahtet = new WeakSet<Document>();

/** „seit 12 Min“, „seit 1 Std 5 Min“ — nie negativ, unter einer Minute „seit <1 Min“. */
export function seitText(ms: number): string {
  const min = Math.max(0, Math.floor(ms / 60_000));
  if (min < 1) return "seit <1 Min";
  if (min < 60) return `seit ${min} Min`;
  const std = Math.floor(min / 60);
  const rest = min % 60;
  return rest === 0 ? `seit ${std} Std` : `seit ${std} Std ${rest} Min`;
}

/** „34 Min“, „1 Std 5 Min“, „<1 Min“ — die Dauer ohne „seit“. */
export function dauerText(ms: number): string {
  return seitText(ms).replace(/^seit /, "");
}

/** „08:12“ — die Startzeit nach der Uhr dieser Seite. */
export function uhrzeit(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Die Zahl auf der Marke: nur aktive Arbeiten (alles außer „fertig“). */
export function aktiveZahl(liste: readonly Pick<SchmiedArbeit, "stufe">[]): number {
  return liste.filter((a) => istAktiv(a.stufe)).length;
}

/** Der Satz für Screenreader an der Marke. */
export function markenSatz(n: number, wartet = 0): string {
  const kern = n === 0 ? "Entwicklung: alles fertig" : `Entwicklung: ${n} laufende ${n === 1 ? "Arbeit" : "Arbeiten"}`;
  return wartet > 0 ? `${kern}, ${wartet} ${wartet === 1 ? "wartet" : "warten"} auf dich` : kern;
}

/** Zeile unter dem Titel: „7 arbeiten, 1 wartet auf dich, 2 fertig“ — nur Teile, die es gibt. */
export function summenSatz(liste: readonly Pick<SchmiedArbeit, "stufe">[]): string {
  const wartet = liste.filter((a) => a.stufe === "wartet").length;
  const fertig = liste.filter((a) => a.stufe === "fertig").length;
  const arbeiten = liste.length - wartet - fertig;
  const teile: string[] = [];
  if (arbeiten > 0) teile.push(`${arbeiten} ${arbeiten === 1 ? "arbeitet" : "arbeiten"}`);
  if (wartet > 0) teile.push(`${wartet} ${wartet === 1 ? "wartet" : "warten"} auf dich`);
  if (fertig > 0) teile.push(`${fertig} fertig`);
  return teile.join(", ");
}

/** Reihenfolge der Tafel: wer auf Martin wartet zuerst, dann laufende (älteste oben, damit nichts springt), „fertig“ zuletzt. */
export function ordneArbeiten<T extends Pick<Gemerkt, "stufe" | "start">>(liste: readonly T[]): T[] {
  const rang = (a: T): number => (a.stufe === "wartet" ? 0 : istAktiv(a.stufe) ? 1 : 2);
  return [...liste].sort((a, b) => rang(a) - rang(b) || (rang(a) === 2 ? b.start - a.start : a.start - b.start));
}

/** Die kleine Zeile unter dem Titel: „baut · ab 08:12 · 34 Min“, „fertig · nach 34 Min“. */
export function standZeile(a: Pick<Gemerkt, "stufe" | "start" | "dauerMs">, jetzt: number): string {
  const name = SCHMIED_ARBEIT_STUFE_NAME[a.stufe];
  if (a.stufe === "fertig") return `${name} · nach ${dauerText(a.dauerMs ?? 0)}`;
  return `${name} · ab ${uhrzeit(a.start)} · ${dauerText(jetzt - a.start)}`;
}

/**
 * 20.10.2026: Unter dem Kopf liegen zwei Tafeln an derselben Stelle (diese und „Offen“, lib/offene-fragen.ts). Wer seine öffnet, meldet
 * es mit der eigenen Listen-ID; jede andere schließt sich. EIN Ereignis statt gegenseitiger Aufrufe.
 */
export const TAFEL_AUF = "voice-v8:kopf-tafel-auf";
export function meldeTafelAuf(doc: Document, id: string): void {
  try {
    doc.dispatchEvent(new CustomEvent<string>(TAFEL_AUF, { detail: id }));
  } catch {
    /* ohne CustomEvent: nichts */
  }
}

function marke(doc: Document): HTMLButtonElement | null {
  const el = doc.getElementById(MARKE_ID);
  return el instanceof HTMLButtonElement ? el : null;
}

export function schliesseArbeitsliste(doc: Document = document): void {
  const m = marke(doc);
  const liste = doc.getElementById(LISTE_ID);
  if (schliessUhr) clearTimeout(schliessUhr);
  schliessUhr = null;
  if (liste) liste.hidden = true;
  m?.setAttribute("aria-expanded", "false");
}

function oeffne(doc: Document): void {
  const m = marke(doc);
  const liste = doc.getElementById(LISTE_ID);
  if (!m || !liste || m.hidden) return;
  if (schliessUhr) clearTimeout(schliessUhr);
  schliessUhr = null;
  const warZu = liste.hidden;
  liste.hidden = false;
  zeigeEntwicklungModell(doc);
  m.setAttribute("aria-expanded", "true");
  if (warZu) meldeTafelAuf(doc, LISTE_ID);
}

function offen(doc: Document): boolean {
  return marke(doc)?.getAttribute("aria-expanded") === "true";
}

function schliesseSpaeter(doc: Document): void {
  if (schliessUhr) clearTimeout(schliessUhr);
  schliessUhr = setTimeout(() => {
    const m = marke(doc);
    const liste = doc.getElementById(LISTE_ID);
    // Wer mit der Tastatur darin steht, behält die Liste.
    if (m && liste && (liste.contains(doc.activeElement) || m.matches(":focus-visible"))) return;
    schliesseArbeitsliste(doc);
  }, SCHLIESS_VERZOEGERUNG_MS);
}

function verdrahte(doc: Document, m: HTMLButtonElement, liste: HTMLElement): void {
  if (verdrahtet.has(m)) return;
  verdrahtet.add(m);
  const nurMaus = (e: PointerEvent): boolean => e.pointerType === "mouse";
  for (const el of [m, liste]) {
    el.addEventListener("pointerenter", (e) => {
      if (nurMaus(e)) oeffne(doc);
    });
    el.addEventListener("pointerleave", (e) => {
      if (nurMaus(e)) schliesseSpaeter(doc);
    });
  }
  // Tastatur: Fokus zeigt die Liste; ein Tipp mit Finger oder Maus löst :focus-visible nicht aus und läuft über click.
  m.addEventListener("focus", () => {
    if (m.matches(":focus-visible")) oeffne(doc);
  });
  m.addEventListener("click", () => (offen(doc) ? schliesseArbeitsliste(doc) : oeffne(doc)));
  if (dokumentVerdrahtet.has(doc)) return;
  dokumentVerdrahtet.add(doc);
  // Die Hörer am Dokument suchen Marke und Liste jedes Mal neu — sie hängen an keinem alten Element.
  doc.addEventListener(TAFEL_AUF, (e) => {
    if ((e as CustomEvent<string>).detail !== LISTE_ID && offen(doc)) schliesseArbeitsliste(doc);
  });
  doc.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !offen(doc)) return;
    const knopf = marke(doc);
    const tafel = doc.getElementById(LISTE_ID);
    schliesseArbeitsliste(doc);
    if (knopf && tafel?.contains(doc.activeElement)) knopf.focus();
  });
  doc.addEventListener("pointerdown", (e) => {
    if (!offen(doc)) return;
    const ziel = e.target;
    if (ziel instanceof Node && (marke(doc)?.contains(ziel) || doc.getElementById(LISTE_ID)?.contains(ziel))) return;
    schliesseArbeitsliste(doc);
  });
}

function zeichne(doc: Document): void {
  const m = marke(doc);
  const zahl = doc.getElementById(ZAHL_ID);
  const liste = doc.getElementById(LISTE_ID);
  const ul = liste?.querySelector("ul");
  if (!m || !zahl || !liste || !ul) return;
  verdrahte(doc, m, liste);
  beobachteEntwicklungModell(doc);
  if (arbeiten.length === 0) {
    m.hidden = true;
    zahl.textContent = "";
    delete m.dataset.wartet;
    ul.replaceChildren();
    schliesseArbeitsliste(doc);
    return;
  }
  const n = aktiveZahl(arbeiten);
  const wartet = arbeiten.filter((a) => a.stufe === "wartet").length;
  m.hidden = false;
  // Nur noch „fertig“ in der Liste: ein Haken statt „0“.
  zahl.textContent = n === 0 ? "\u2713" : String(n);
  if (wartet > 0) m.dataset.wartet = "1";
  else delete m.dataset.wartet;
  m.setAttribute("aria-label", markenSatz(n, wartet));
  const summe = doc.getElementById(SUMME_ID);
  if (summe) summe.textContent = summenSatz(arbeiten);
  const jetzt = Date.now();
  const zeilen = ordneArbeiten(arbeiten).map((a) => {
    const li = doc.createElement("li");
    li.className = "topbar-entwicklung-zeile";
    li.dataset.stand = a.stufe;
    const zeichen = doc.createElement("span");
    zeichen.className = "topbar-entwicklung-zeichen";
    zeichen.setAttribute("aria-hidden", "true");
    const was = doc.createElement("span");
    was.className = "topbar-entwicklung-was";
    was.textContent = a.stichwort;
    const meta = doc.createElement("span");
    meta.className = "topbar-entwicklung-meta";
    meta.textContent = standZeile(a, jetzt);
    li.append(zeichen, was, meta);
    return li;
  });
  ul.replaceChildren(...zeilen);
}

/** Zeigt den Stand (keine Arbeiten = Marke verborgen). Ohne die Elemente (andere Seite) passiert nichts. */
export function zeigeSchmiedArbeit(stand: SchmiedArbeitStand, doc: Document = document): void {
  if (!marke(doc)) return;
  const jetzt = Date.now();
  arbeiten = stand.arbeiten.slice(0, SCHMIED_ARBEIT_HOECHSTENS).map((a) => ({
    id: a.id,
    stichwort: a.stichwort,
    stufe: a.stufe,
    start: jetzt - a.seitMs,
    ...(a.stufe === "fertig" ? { dauerMs: a.seitMs } : {}),
  }));
  if (verfallsUhr) clearTimeout(verfallsUhr);
  verfallsUhr = null;
  if (auffrischUhr) clearInterval(auffrischUhr);
  auffrischUhr = null;
  zeichne(doc);
  if (arbeiten.length === 0) return;
  // Verfällt auch hier von selbst, wenn der Server nach einem Abriss kein Ende mehr schicken konnte.
  const rest = Math.max(0, Math.min(...stand.arbeiten.map((a) => SCHMIED_ARBEIT_VERFALL_MS - a.seitMs)));
  verfallsUhr = setTimeout(() => zeigeSchmiedArbeit({ arbeiten: [] }, doc), rest);
  auffrischUhr = setInterval(() => zeichne(doc), AUFFRISCHEN_MS);
}
